/**
 * 丽呈账户列表（账户管理 → 丽呈账户列表）
 *
 * 定位：账户列表中「丽呈」主信息（appid 等）的子列表，用于维护丽呈「卡」。
 * 与账户列表的差异：
 *   - 去掉 App ID、Api Key（主信息在账户列表维护）
 *   - 增加「卡号」列 / 搜索项
 *   - 类型固定为「丽呈」，不可切换
 *   - 编辑不走「操作密码」验证
 *
 * 数据源：GET /api/backend/api-account/list?api_type=li_cheng
 * 说明：卡号字段名后端尚未最终确定，前端按以下别名依次兼容（见 pickCardNo）：
 *       card_no / card_number / cardNo / card_id / card_code / no
 *       后端定稿后只需保留其一，其余可清理。
 */
<template>
  <div class="app-container">
    <div class="filter-container">
      <el-button
        class="filter-item"
        type="primary"
        icon="el-icon-edit"
        @click="handleCreate"
        v-if="$canEdit"
        size="small"
        >添加</el-button
      >
      <el-input
        class="filter-item"
        v-model="listQuery.card_no"
        placeholder="请输入卡号"
        clearable
        style="width: 220px"
        size="small"
        @keyup.enter.native="handleFilter"
      />
      <el-select
        v-model="listQuery.status"
        placeholder="状态"
        style="width: 120px"
        class="filter-item"
        size="small"
        clearable
      >
        <el-option label="正常" :value="'enable'" />
        <el-option label="禁用" :value="'disable'" />
        <el-option label="错误禁用" :value="'error_disable'" />
      </el-select>
      <el-select
        v-model="listQuery.is_backup"
        placeholder="是否可备用"
        style="width: 120px"
        class="filter-item"
        size="small"
        clearable
      >
        <el-option label="是" :value="1" />
        <el-option label="否" :value="0" />
      </el-select>
      <el-button
        class="filter-item"
        icon="el-icon-search"
        :loading="listLoading"
        type="primary"
        @click="handleFilter"
        size="small"
        >搜索
      </el-button>
    </div>

    <!-- 卡号字段尚未由后端提供时给出提示，避免被误认为页面缺陷 -->
    <el-alert
      v-if="cardNoMissing"
      class="card-missing-tip"
      type="warning"
      :closable="false"
      show-icon
      title="卡号字段待后端接口提供：当前列表「卡号」列显示为 -，接口返回卡号字段后自动显示。"
    />

    <el-table
      v-loading="listLoading"
      :data="list"
      element-loading-text="Loading"
      border
      fit
      highlight-current-row
      size="small"
    >
      <el-table-column align="center" label="ID" width="95">
        <template slot-scope="scope">
          {{ scope.row.id }}
        </template>
      </el-table-column>
      <el-table-column label="类型" min-width="110" align="center">
        <template slot-scope="scope">
          <el-tag :type="getTargetTypeTagType(scope.row.api_type || 'li_cheng')">
            {{ getTargetTypeName(scope.row.api_type || 'li_cheng') }}
          </el-tag>
        </template>
      </el-table-column>
      <el-table-column label="名称" min-width="110" align="center">
        <template slot-scope="scope">
          {{ scope.row.name || '-' }}
        </template>
      </el-table-column>
      <!-- 卡号：字段名待后端定稿，前端做别名兼容 -->
      <el-table-column label="卡号" min-width="180" align="center">
        <template slot-scope="scope">
          {{ pickCardNo(scope.row) || '-' }}
        </template>
      </el-table-column>
      <el-table-column label="绑定合作商" min-width="200" align="center">
        <template slot-scope="scope">
          <el-tag
            v-for="(item, index) in scope.row.app_clients || []"
            :key="index"
            effect="plain"
            class="client-tag"
          >
            {{ item.name }}
          </el-tag>
          {{ !scope.row.app_clients || scope.row.app_clients.length <= 0 ? '-' : '' }}
        </template>
      </el-table-column>
      <el-table-column label="状态" min-width="110" align="center">
        <template slot-scope="scope">
          <el-tag v-if="scope.row.status == 'enable'" type="success"
            >启用</el-tag
          >
          <el-tag v-else-if="scope.row.status == 'disable'" type="warning"
            >禁用</el-tag
          >
          <el-tag v-else-if="scope.row.status == 'error_disable'" type="danger"
            >错误禁用</el-tag
          >
          <span v-else>-</span>
        </template>
      </el-table-column>
      <el-table-column prop="remark" label="备注" min-width="140">
        <template slot-scope="scope">
          <el-tooltip
            placement="top"
            :disabled="!scope.row.remark || scope.row.remark.length <= 20"
          >
            <div style="max-width: 200px; text-align: center" slot="content">
              {{ scope.row.remark || '-' }}
            </div>
            <span class="remark-cell">
              {{
                scope.row.remark
                  ? scope.row.remark.length > 20
                    ? scope.row.remark.substring(0, 20) + '...'
                    : scope.row.remark
                  : '-'
              }}
            </span>
          </el-tooltip>
        </template>
      </el-table-column>
      <el-table-column label="是否可备用" min-width="110" align="center">
        <template slot-scope="scope">
          <el-tag v-if="scope.row.is_backup == 1" type="success">是</el-tag>
          <el-tag v-else-if="scope.row.is_backup == 0" type="warning"
            >否</el-tag
          >
        </template>
      </el-table-column>
      <el-table-column label="错误数" min-width="110" align="center">
        <template slot-scope="scope">
          {{ scope.row.error_num }}
        </template>
      </el-table-column>
      <el-table-column align="center" label="创建时间" min-width="140">
        <template slot-scope="scope">
          <i class="el-icon-time" />
          <span>{{ scope.row.created_at || '-' }}</span>
        </template>
      </el-table-column>
      <el-table-column align="center" label="更新时间" min-width="140">
        <template slot-scope="scope">
          <i class="el-icon-time" />
          <span>{{ scope.row.updated_at || '-' }}</span>
        </template>
      </el-table-column>
      <el-table-column
        label="操作"
        align="center"
        width="230"
        v-if="$canEdit"
        class-name="small-padding fixed-width"
      >
        <template slot-scope="scope">
          <!-- 需求：编辑不走操作密码验证，直接打开编辑弹窗 -->
          <el-button size="mini" type="primary" @click="handleUpdate(scope.row)"
            >编辑</el-button
          >
          <el-button size="mini" type="danger" @click="handleDel(scope.row.id)"
            >删除</el-button
          >
        </template>
      </el-table-column>
    </el-table>

    <div class="pagination-container">
      <el-pagination
        :current-page="listQuery.page"
        :page-sizes="[10, 20, 30, 50]"
        :page-size="listQuery.limit"
        :total="total"
        background
        layout="total, sizes, prev, pager, next, jumper"
        @size-change="handleSizeChange"
        @current-change="handleCurrentChange"
      />
    </div>

    <el-dialog
      :title="textMap[dialogStatus]"
      :visible.sync="dialogFormVisible"
      :close-on-click-modal="false"
    >
      <el-form
        ref="form"
        :rules="rules"
        :model="form"
        label-position="left"
        label-width="90px"
        style="width: 400px; margin-left: 50px"
      >
        <el-form-item label="类型" prop="api_type">
          <!-- 类型固定丽呈 -->
          <el-tag :type="getTargetTypeTagType('li_cheng')">{{
            getTargetTypeName('li_cheng')
          }}</el-tag>
        </el-form-item>
        <el-form-item label="名称" prop="name">
          <el-input v-model="form.name" placeholder="请输入名称" />
        </el-form-item>
        <el-form-item label="卡号" prop="card_no">
          <el-input v-model="form.card_no" placeholder="请输入卡号" />
        </el-form-item>
        <el-form-item label="状态" prop="status">
          <el-select v-model="form.status" style="width: 100%">
            <el-option label="启用" :value="'enable'" />
            <el-option label="禁用" :value="'disable'" />
            <el-option label="错误禁用" :value="'error_disable'" />
          </el-select>
        </el-form-item>
        <el-form-item label="是否可备用" prop="is_backup">
          <el-switch v-model="form.is_backup" />
        </el-form-item>
        <el-form-item label="备注" prop="remark">
          <el-input v-model="form.remark" type="textarea" :rows="3" />
        </el-form-item>
      </el-form>
      <div slot="footer" class="dialog-footer">
        <el-button @click="dialogFormVisible = false">取消</el-button>
        <el-button type="primary" @click="saveData" :loading="btnLoading"
          >确定</el-button
        >
      </div>
    </el-dialog>
  </div>
</template>

<script>
import request from '@/utils/request'
import {
  getTargetTypeName,
  getTargetTypeTagType,
} from '@/utils/targetType'

/** 丽呈类型标识 */
const API_TYPE = 'li_cheng'

export default {
  name: 'LiChengAccounts',
  data() {
    return {
      list: null,
      total: null,
      listLoading: true,
      listQuery: {
        page: 1,
        limit: 10,
        // 固定只查丽呈
        api_type: API_TYPE,
        card_no: '',
        status: '',
        is_backup: '',
      },
      dialogFormVisible: false,
      dialogStatus: '',
      textMap: {
        update: '编辑',
        create: '添加',
      },
      form: {
        id: '',
        name: '',
        card_no: '',
        status: 'enable',
        is_backup: 0,
        remark: '',
      },
      rules: {
        name: [{ required: true, message: '名称不能为空', trigger: 'blur' }],
        card_no: [{ required: true, message: '卡号不能为空', trigger: 'blur' }],
        status: [{ required: true, message: '状态不能为空', trigger: 'change' }],
      },
      btnLoading: false,
    }
  },
  computed: {
    /** 列表有数据但所有行都取不到卡号 → 提示字段未就绪 */
    cardNoMissing() {
      if (!this.list || !this.list.length) return false
      return this.list.every((row) => !this.pickCardNo(row))
    },
  },
  created() {
    this.getList()
  },
  methods: {
    getTargetTypeName: getTargetTypeName,
    getTargetTypeTagType: getTargetTypeTagType,

    /**
     * 读取卡号：后端字段名尚未定稿，按别名依次兼容
     * 定稿后建议只保留实际字段名
     */
    pickCardNo(row) {
      if (!row) return ''
      const keys = [
        'card_no',
        'card_number',
        'cardNo',
        'card_id',
        'card_code',
        'no',
      ]
      for (const k of keys) {
        const v = row[k]
        if (v !== undefined && v !== null && v !== '') return v
      }
      return ''
    },

    getList() {
      this.listLoading = true
      const params = { ...this.listQuery, api_type: API_TYPE }
      // 空值不传，避免后端把空串当筛选条件
      Object.keys(params).forEach((k) => {
        if (params[k] === '' || params[k] === undefined || params[k] === null) {
          delete params[k]
        }
      })
      request({
        url: '/api/backend/li-cheng-card/list',
        method: 'get',
        params,
      })
        .then((response) => {
          this.list = response.data.data
          this.total = response.data.total
        })
        .finally(() => {
          this.listLoading = false
        })
    },
    handleFilter() {
      this.listQuery.page = 1
      this.getList()
    },
    handleSizeChange(val) {
      this.listQuery.limit = val
      this.getList()
    },
    handleCurrentChange(val) {
      this.listQuery.page = val
      this.getList()
    },
    resetForm() {
      this.form = {
        id: '',
        name: '',
        card_no: '',
        status: 'enable',
        is_backup: 0,
        remark: '',
      }
      this.btnLoading = false
    },

    handleCreate() {
      this.resetForm()
      this.dialogStatus = 'create'
      this.dialogFormVisible = true
      this.$nextTick(() => {
        this.$refs['form'].clearValidate()
      })
    },
    // 编辑：不校验操作密码，直接进入表单
    handleUpdate(row) {
      this.resetForm()
      this.form = Object.assign({}, row, {
        card_no: this.pickCardNo(row),
        is_backup: Boolean(row.is_backup),
      })
      this.dialogStatus = 'update'
      this.dialogFormVisible = true
      this.$nextTick(() => {
        this.$refs['form'].clearValidate()
      })
    },
    saveData() {
      this.$refs['form'].validate((valid) => {
        if (!valid) return
        this.btnLoading = true
        request({
          url: '/api/backend/li-cheng-card/store',
          method: 'post',
          data: {
            ...this.form,
            api_type: API_TYPE,
            is_backup: Number(this.form.is_backup || 0),
          },
        })
          .then(() => {
            this.btnLoading = false
            this.dialogFormVisible = false
            this.$notify({
              title: '成功',
              message: '保存成功',
              type: 'success',
              duration: 2000,
            })
            this.getList()
          })
          .catch(() => {
            // 错误提示由请求拦截器统一弹出，此处仅恢复按钮状态
            this.btnLoading = false
          })
      })
    },
    handleDel(val) {
      this.$confirm('确定要删除该丽呈卡, 是否继续?', '提示', {
        type: 'warning',
      })
        .then(() => {
          request({
            url: '/api/backend/li-cheng-card/delete',
            method: 'post',
            data: { id: val },
          }).then(() => {
            this.getList()
            this.$message({
              type: 'success',
              message: '删除成功!',
            })
          })
        })
        .catch(() => {
          this.$message({
            type: 'info',
            message: '已取消',
          })
        })
    },
  },
}
</script>

<style scoped>
.client-tag {
  margin: 1px 3px 1px 0;
}

.card-missing-tip {
  margin-bottom: 12px;
}
</style>
